import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { deleteGame } from "@/lib/stores/unifiedUserStore";
import { Input } from "@/components/ui/InputDefault";
import { Button } from "@/components/ui/button";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface DeleteGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  gameName: string;
  onGameDeleted?: () => void;
}

export default function DeleteGameModal({ 
  isOpen, 
  onClose, 
  gameId,
  gameName,
  onGameDeleted 
}: DeleteGameModalProps) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const expectedText = `Delete ${gameName}`;
  const isConfirmValid = confirmText === expectedText;

  const resetForm = () => {
    setConfirmText("");
  };

  const handleClose = () => {
    if (!isDeleting) {
      resetForm();
      onClose();
    }
  };

  const handleDeleteGame = async () => {
    if (!isConfirmValid) {
      toast({
        title: "Error",
        description: `Please type "${expectedText}" to confirm`,
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await deleteGame(gameId, toast);
      
      resetForm();
      onClose();
      
      // Notify parent component that the game was deleted
      if (onGameDeleted) {
        onGameDeleted();
      }
    } catch (error) {
      console.error("Error deleting game:", error);
      // Error handling is done in deleteGame
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-center text-destructive">{t('platform.settings.deletegamemodal.delete_game')}</DialogTitle>
          <DialogDescription className="text-center">
            This action cannot be undone. This will permanently delete the game and all associated data.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded p-4">
            <p className="text-sm text-foreground font-medium mb-2">
              You are about to delete:
            </p>
            <p className="text-lg font-semibold text-destructive">
              {gameName}
            </p>
          </div>
          <div>
            <label htmlFor="confirm-text" className="block text-sm font-medium text-foreground mb-2">
              Type <span className="font-mono font-bold">Delete {gameName}</span> to confirm *
            </label>
            <Input
              id="confirm-text"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expectedText}
              className="w-full"
              disabled={isDeleting}
              autoComplete="off"
            />
          </div>
        </div>
        <DialogFooter className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteGame}
            disabled={isDeleting || !isConfirmValid}
          >
            {isDeleting ? "Deleting..." : "Delete Game"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

